import request from 'sb-request/graphql'
import { object } from 'common/helpers/primitives'

import availablePlansQuery from './availablePlans.graphql'
import planSizesQuery from './planSizes.graphql'
import changeSubscriptionPlanQuery from './changeSubscriptionPlan.graphql'
import subscriptionChangePlanInquiryQuery from './subscriptionChangePlanInquiry.graphql'
import subscriptionChangePlanSessionQuery from './subscriptionChangePlanSession.graphql'
import changeFrequencyPlansQuery from './changeFrequencyPlans.graphql'
import downgradePlansQuery from './downgradePlans.graphql'
import upgradePlansQuery from './upgradePlans.graphql'
import refundInquiryQuery from './refundInquiry.graphql'
import skipSubscriptionQuery from './skipSubscription.graphql'
import unskipSubscriptionQuery from './unskipSubscription.graphql'
import subscriptionUnsubscribeQuery from './subscriptionUnsubscribe.graphql'
import resubscribeQuery from './resubscribe.graphql'
import subscriptionCouponQuery from './subscriptionCoupon.graphql'

import { AvailablePlansVariables, AvailablePlansPayload } from './availablePlansGQL'
import { PlanSizesVariables, PlanSizesPayload } from './planSizesGQL'
import { ChangeSubscriptionPlanVariables, ChangeSubscriptionPlanPayload } from './changeSubscriptionPlanGQL'
import { SubscriptionChangePlanInquiryVariables, SubscriptionChangePlanInquiryPayload } from './subscriptionChangePlanInquiryGQL'
import { SubscriptionChangePlanSessionVariables, SubscriptionChangePlanSessionPayload } from './subscriptionChangePlanSessionGQL'
import { ChangeFrequencyPlansPayload } from './changeFrequencyPlansGQL'
import { DowngradePlansPayload } from './downgradePlansGQL'
import { UpgradePlansPayload } from './upgradePlansGQL'
import { RefundInquiryVariables, RefundInquiryPayload } from './refundInquiryGQL'
import { SkipSubscriptionVariables, SkipSubscriptionPayload } from './skipSubscriptionGQL'
import { UnskipSubscriptionVariables, UnskipSubscriptionPayload } from './unskipSubscriptionGQL'
import { SubscriptionUnsubscribeVariables, SubscriptionUnsubscribePayload } from './subscriptionUnsubscribeGQL'
import { ResubscribeVariables, ResubscribePayload } from './resubscribeGQL'
import { SubscriptionCouponPayload } from './subscriptionCouponGQL'


type AvailablePlansResult = {
  data: AvailablePlansPayload['currentUser']['data']['subscription']['changePlansInquiry']
}

export const getAvailablePlans = (variables: AvailablePlansVariables) =>
  request<AvailablePlansVariables, AvailablePlansPayload, AvailablePlansResult>('getAvailablePlans', {
    query: availablePlansQuery,
    variables,
  }, {
    modifyResult: ({ currentUser }) => ({
      data: object.get(currentUser, 'data.subscription.changePlansInquiry'),
    }),
  })

export type GetChangeFrequencyPlansResult = {
  data: ChangeFrequencyPlansPayload['currentUser']['data']['subscription']['changePlansInquiry']['changeFrequencyPlans']
  error: ChangeFrequencyPlansPayload['currentUser']['error']
}

export const getChangeFrequencyPlans = (): Promise<GetChangeFrequencyPlansResult> =>
  request<never, ChangeFrequencyPlansPayload, GetChangeFrequencyPlansResult>('getChangeFrequencyPlans', {
    query: changeFrequencyPlansQuery,
  }, {
    modifyResult: ({ currentUser }) => ({
      data: object.get(currentUser, 'data.subscription.changePlansInquiry.changeFrequencyPlans'),
      error: object.get(currentUser, 'error'),
    }),
  })


export type DowngradePlansResult = {
  data: DowngradePlansPayload['currentUser']['data']['subscription']['changePlansInquiry']['downgradePlans']
}

export const getDowngradePlans = (): Promise<DowngradePlansResult> =>
  request<never, DowngradePlansPayload, DowngradePlansResult>('getDowngradePlans', {
    query: downgradePlansQuery,
  }, {
    modifyResult: ({ currentUser: { data } }) => ({
      data: object.get(data, 'subscription.changePlansInquiry.downgradePlans'),
    }),
  })


export type UpgradePlansResult = {
  data: UpgradePlansPayload['currentUser']['data']['subscription']['changePlansInquiry']['upgradePlans']
}

export const getUpgradePlans = (): Promise<UpgradePlansResult> =>
  request<never, UpgradePlansPayload, UpgradePlansResult>('getUpgradePlans', {
    query: upgradePlansQuery,
  }, {
    modifyResult: (data) => ({
      data: object.get(data, 'currentUser.data.subscription.changePlansInquiry.upgradePlans'),
    }),
  })


type PlanSizesResult = {
  data: PlanSizesPayload['currentUser']['data']['plansInquiry']
}

export const getPlanSizes = (input: PlanSizesVariables['input']) =>
  request<PlanSizesVariables, PlanSizesPayload, PlanSizesResult>('getPlanSizes', {
    query: planSizesQuery,
    variables: {
      input,
    },
  }, {
    modifyResult: ({ currentUser }) => ({
      data: object.get(currentUser, 'data.plansInquiry'),
    }),
  })


export type GetSubscriptionPlanCostsResult = {
  data: SubscriptionChangePlanInquiryPayload['subscriptionChangePlanInquiry']['data']
  error: { code: string, message: string }
}

export const getSubscriptionPlanCosts = (input: SubscriptionChangePlanInquiryVariables['input']): Promise<GetSubscriptionPlanCostsResult> =>
  request<
    SubscriptionChangePlanInquiryVariables,
    SubscriptionChangePlanInquiryPayload,
    GetSubscriptionPlanCostsResult
  >('getSubscriptionPlanCosts', {
    query: subscriptionChangePlanInquiryQuery,
    variables: { input },
  }, {
    modifyResult: ({ subscriptionChangePlanInquiry }) => subscriptionChangePlanInquiry,
  })


export type ChangeSubscriptionPlanResult = {
  session: ChangeSubscriptionPlanPayload['subscriptionChangePlan']['session']
  data: ChangeSubscriptionPlanPayload['subscriptionChangePlan']['data']
  error: { code: string, message: string }
}

export const changeSubscriptionPlan = (input: ChangeSubscriptionPlanVariables['input']) =>
  request<
    ChangeSubscriptionPlanVariables,
    ChangeSubscriptionPlanPayload,
    ChangeSubscriptionPlanResult
  >('changeSubscriptionPlan', {
    query: changeSubscriptionPlanQuery,
    variables: { input },
  }, {
    modifyResult: ({ subscriptionChangePlan }) => subscriptionChangePlan,
  })


type SubscriptionCouponResult = {
  data: SubscriptionCouponPayload['currentUser']['data']['subscription']['coupon']
  error: { code: string, message: string }
}

export const getSubscriptionCoupon = () =>
  request<never, SubscriptionCouponPayload, SubscriptionCouponResult>('getSubscriptionCoupon', {
    query: subscriptionCouponQuery,
  }, {
    modifyResult: ({ currentUser }) => ({
      data: object.get(currentUser, 'data.subscription.coupon'),
      error: currentUser.error,
    }),
  })


export type RefundInquiryResult = RefundInquiryPayload['currentUser']['data']['subscription']['refundInquiry']

export const refundInquiry = (input: RefundInquiryVariables['input']) =>
  request<
    RefundInquiryVariables,
    RefundInquiryPayload,
    RefundInquiryResult
  >('refundInquiry', {
    query: refundInquiryQuery,
    variables: { input },
  }, {
    modifyResult: (data) => object.get(data, 'currentUser.data.subscription.refundInquiry'),
  })


export type SkipSubscriptionResult = {
  data: SkipSubscriptionPayload['subscriptionSkip']['data']
  error: { code: string, message: string }
}

export const skipSubscription = (input: SkipSubscriptionVariables['input']) =>
  request<
    SkipSubscriptionVariables,
    SkipSubscriptionPayload,
    SkipSubscriptionResult
  >('skipSubscription', {
    query: skipSubscriptionQuery,
    variables: { input },
  }, {
    modifyResult: ({ subscriptionSkip }) => subscriptionSkip,
  })

type UnskipSubscriptionResult = {
  data: UnskipSubscriptionPayload['subscriptionUnskip']['data']
  error?: {
    code: string
    message: string
  }
}

export const unskipSubscription = (input: UnskipSubscriptionVariables['input']) =>
  request<UnskipSubscriptionVariables, UnskipSubscriptionPayload, UnskipSubscriptionResult>('unskipSubscription', {
    query: unskipSubscriptionQuery,
    variables: {
      input,
    },
  }, {
    modifyResult: ({ subscriptionUnskip }) => subscriptionUnskip,
  })


export type SubscriptionUnsubscribeResult = {
  data: SubscriptionUnsubscribePayload['subscriptionUnsubscribe']['data']
  error: { code: string, message: string }
}

export const subscriptionUnsubscribe = (input: SubscriptionUnsubscribeVariables['input']) =>
  request<
    SubscriptionUnsubscribeVariables,
    SubscriptionUnsubscribePayload,
    SubscriptionUnsubscribeResult
  >('subscriptionUnsubscribe', {
    query: subscriptionUnsubscribeQuery,
    variables: {
      input,
    },
  }, {
    modifyResult: ({ subscriptionUnsubscribe }) => subscriptionUnsubscribe,
  })


type ResubscribeResult = Omit<ResubscribePayload['subscriptionResubscribe'], 'error'> & {
  error: { code: string, message: string }
}

export const resubscribe = (input: ResubscribeVariables['input'], config = {}) =>
  request<ResubscribeVariables, ResubscribePayload, ResubscribeResult>('resubscribe', {
    query: resubscribeQuery,
    variables: {
      input,
    },
  }, {
    ...config,
    modifyResult: ({ subscriptionResubscribe }) => subscriptionResubscribe,
  })
